import { StreamingHttpError } from '@luminovo/api-contracts';

/**
 * A `fetch` for endpoints that stream their answer, for SDKs that take one, such as the AI SDK's chat transport. Each
 * request carries the token `getToken` returns at that moment; an error answer throws `StreamingHttpError`, and any
 * other answer comes back unread. `http()` can't do this: it reads and checks the whole body.
 */
export function createStreamingFetch(
    getToken: () => string,
): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
    return async (input, init) => {
        const headers = new Headers(init?.headers);
        headers.set('Authorization', `Bearer ${getToken()}`);
        const response = await fetch(input, { ...init, headers });
        if (!response.ok) {
            const body: { code?: unknown } = await response.json().catch(() => ({}));
            throw new StreamingHttpError({
                status: response.status,
                code: typeof body.code === 'string' ? body.code : undefined,
                retryAt: retryAt(response.headers.get('Retry-After')),
            });
        }
        return response;
    };
}

function retryAt(header: string | null): Date | undefined {
    if (header === null || header.trim() === '') {
        return undefined;
    }
    const seconds = Number(header);
    if (Number.isFinite(seconds)) {
        return new Date(Date.now() + seconds * 1000);
    }
    const date = Date.parse(header);
    return Number.isNaN(date) ? undefined : new Date(date);
}
